import React from "react"
import Box from "@mui/material/Box";
import {CmCollectionType} from "../../types";
import CCOComponentWrapper from "../../fundamentals/CCOComponentWrapper";
import CCOContainer from "../../fundamentals/CCOContainer";
import TrustElementCard, {
    DEFAULT_TRUST_ELEMENT_TRANSITION_DURATION,
    preloadTrustElementMedia,
    type TrustElementItem,
} from "./TrustElementCard";
import getSetting, {
    SETTING_GALLERY_AUTOPLAY,
    SETTING_GALLERY_AUTOPLAY_SPEED,
    SETTING_GALLERY_IMAGESLIDE_SPEED
} from "../../../pages/config/settings";
import {usePreviewContext} from "../../../context/PreviewContext";

export interface Props {
    cmCollection: CmCollectionType
}

const DEFAULT_AUTOPLAY_INTERVAL = 5000;

type TeaserCarouselSettings = {
    [SETTING_GALLERY_AUTOPLAY]?: boolean | number | string | null
    [SETTING_GALLERY_AUTOPLAY_SPEED]?: number | string | null
    [SETTING_GALLERY_IMAGESLIDE_SPEED]?: number | string | null
};

type NumericCarouselSetting =
    | typeof SETTING_GALLERY_AUTOPLAY_SPEED
    | typeof SETTING_GALLERY_IMAGESLIDE_SPEED;

const trustElementFrameSx = {
    width: '100%',
    maxWidth: {
        xs: '100%',
        sm: '559px',
        md: '559px',
        lg: '696px',
    },
    mx: 'auto',
};

const trustElementSlideSx = {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    overflow: 'visible',
};

const trustElementCarouselSx = {
    ...trustElementFrameSx,
    height: {
        xs: '138px',
        sm: '138px',
        md: '144px',
        lg: '181px',
    },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
};

const trustElementSlideContentSx = {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
};

const trustElementIndicatorsSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: {xs: '22px', sm: '22px', md: 0},
    width: 'fit-content',
    height: {
        xs: '8px',
        sm: '8px',
        md: '10px',
    },
    mt: '13px',
    mx: 'auto',
};

const trustElementIndicatorSx = (active: boolean) => ({
    position: 'relative',
    width: {xs: '8px', sm: '8px', md: '32px'},
    height: {xs: '8px', sm: '8px', md: '32px'},
    flex: '0 0 auto',
    border: 0,
    borderRadius: '50%',
    padding: 0,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    '&::before': {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: {xs: '8px', sm: '8px', md: '10px'},
        height: {xs: '8px', sm: '8px', md: '10px'},
        borderRadius: '50%',
        backgroundColor: active ? 'primary.main' : '#afafaf',
        content: '""',
        transform: 'translate(-50%, -50%)',
        transition: 'background-color 240ms ease',
    },
    '&::after': {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: {xs: '30px', sm: '30px', md: '32px'},
        height: {xs: '30px', sm: '30px', md: '32px'},
        borderRadius: '50%',
        content: '""',
        transform: 'translate(-50%, -50%)',
    },
    '&:hover::before': {
        backgroundColor: active ? 'primary.dark' : 'text.primary',
    },
    '&:focus-visible': {
        outline: 0,
    },
    '&:focus-visible::after': {
        outline: '2px solid',
        outlineColor: 'primary.main',
        outlineOffset: '2px',
    },
});

function resolveAutoplay(teaserCarouselSettings?: TeaserCarouselSettings | null) {
    const configuredValue = teaserCarouselSettings?.[SETTING_GALLERY_AUTOPLAY];

    if (configuredValue === undefined || configuredValue === null || configuredValue === '') {
        return true;
    }
    if (typeof configuredValue === 'string') {
        return !['false', '0', 'no'].includes(configuredValue.toLowerCase());
    }
    return Boolean(configuredValue);
}

function resolveNumberSetting(
    teaserCarouselSettings: TeaserCarouselSettings | null | undefined,
    key: NumericCarouselSetting,
    defaultValue: number
) {
    const setting = getSetting(teaserCarouselSettings, key);
    const numberSetting = Number(setting);

    return Number.isFinite(numberSetting) && numberSetting > 0 ? numberSetting : defaultValue;
}

function C093TrustElementCollection(props: Props) {
    const {cmCollection} = props;
    const isPreview = usePreviewContext().isPreview;
    const items = React.useMemo(
        () => (cmCollection.items ?? []).filter(Boolean).slice(0, 3) as TrustElementItem[],
        [cmCollection.items]
    );
    const transitionRequest = React.useRef(0);
    const transitionTimer = React.useRef<number | null>(null);
    const activeIndexRef = React.useRef(0);
    const [activeIndex, setActiveIndex] = React.useState(0);
    const [previousIndex, setPreviousIndex] = React.useState<number | null>(null);
    const [transitionId, setTransitionId] = React.useState(0);
    const teaserCarouselSettings = cmCollection.teaserCarouselSettings as TeaserCarouselSettings | null | undefined;
    const autoplay = resolveAutoplay(teaserCarouselSettings);
    const interval = resolveNumberSetting(
        teaserCarouselSettings,
        SETTING_GALLERY_AUTOPLAY_SPEED,
        DEFAULT_AUTOPLAY_INTERVAL
    );
    const duration = resolveNumberSetting(
        teaserCarouselSettings,
        SETTING_GALLERY_IMAGESLIDE_SPEED,
        DEFAULT_TRUST_ELEMENT_TRANSITION_DURATION
    );

    const transitionTo = React.useCallback((index: number) => {
        const currentIndex = activeIndexRef.current;
        if (index === currentIndex || !items[index]) return;

        if (transitionTimer.current) {
            window.clearTimeout(transitionTimer.current);
        }

        activeIndexRef.current = index;
        setPreviousIndex(currentIndex);
        setActiveIndex(index);
        setTransitionId((currentTransitionId) => currentTransitionId + 1);
        transitionTimer.current = window.setTimeout(() => {
            setPreviousIndex(null);
            transitionTimer.current = null;
        }, duration);
    }, [duration, items]);

    React.useEffect(() => () => {
        if (transitionTimer.current) {
            window.clearTimeout(transitionTimer.current);
        }
    }, []);

    React.useEffect(() => {
        if (items.length <= 1 || !autoplay) return undefined;

        let cancelled = false;
        const timer = window.setTimeout(() => {
            const nextIndex = (activeIndex + 1) % items.length;
            const requestId = ++transitionRequest.current;

            void preloadTrustElementMedia(items[nextIndex], isPreview).finally(() => {
                if (!cancelled && requestId === transitionRequest.current) {
                    transitionTo(nextIndex);
                }
            });
        }, interval);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [activeIndex, autoplay, interval, isPreview, items, transitionTo]);

    React.useEffect(() => {
        if (activeIndex >= items.length) {
            activeIndexRef.current = 0;
            setPreviousIndex(null);
            setActiveIndex(0);
        }
    }, [activeIndex, items.length]);

    React.useEffect(() => {
        items.forEach((item) => {
            void preloadTrustElementMedia(item, isPreview);
        });
    }, [isPreview, items]);

    const selectSlide = React.useCallback((index: number) => {
        transitionRequest.current += 1;
        transitionTo(index);
        void preloadTrustElementMedia(items[index], isPreview);
    }, [isPreview, items, transitionTo]);

    if (items.length === 0) return null;

    function renderSingleItem() {
        return (
            <Box
                data-cypress="trust-element-single"
                data-testid="trust-element-single"
                sx={{...trustElementFrameSx, ...trustElementSlideSx}}
            >
                <TrustElementCard item={items[0]} animationDuration={duration}/>
            </Box>
        );
    }

    function renderCarousel() {
        return (
            <Box
                data-testid="trust-element-carousel"
                data-autoplay={String(autoplay)}
                data-interval={interval}
                data-duration={duration}
                sx={trustElementCarouselSx}
            >
                <Box sx={trustElementSlideSx} data-cypress={`trust-element-carousel-item-${activeIndex}`}>
                    <Box sx={trustElementSlideContentSx}>
                        <TrustElementCard
                            animationDuration={duration}
                            item={items[activeIndex]}
                            previousItem={previousIndex === null ? null : items[previousIndex]}
                            transitionId={transitionId}
                        />
                    </Box>
                </Box>
                <Box data-testid="trust-element-indicators" sx={trustElementIndicatorsSx}>
                    {items.map((item, index) => (
                        <Box
                            key={item?.id ?? index}
                            aria-current={index === activeIndex ? 'true' : undefined}
                            aria-label={`Trust Element ${index + 1}`}
                            component="button"
                            data-testid={`trust-element-indicator-${index}`}
                            onClick={() => selectSlide(index)}
                            sx={trustElementIndicatorSx(index === activeIndex)}
                            type="button"
                        />
                    ))}
                </Box>
            </Box>
        );
    }

    return (
        <CCOComponentWrapper
            background="grey"
            dataCypress="trust-element-collection"
            id="trust-element-collection"
            customStyle={{
                py: 0,
                px: 0,
                height: {
                    xs: '173px',
                    sm: '173px',
                    md: '186px',
                    lg: '240px',
                },
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <CCOContainer
                background="grey"
                width="large"
                sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {items.length === 1 ? renderSingleItem() : renderCarousel()}
            </CCOContainer>
        </CCOComponentWrapper>
    );
}

export default C093TrustElementCollection
